import React, { useEffect, useState } from 'react';
import MapboxMap from '../../MapboxMap/MapboxMap';
import { supabase } from '../../../helpers/Supabase';
import '../../../styles/components/Dashboard.scss';
import zipcodes from 'zipcodes';

interface City {
  name: string;
  state: string;
  lat: number;
  lng: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  storeIds: number[];
  stores: { name: string; hash_id: string }[];
  avgSentiment: 'positive' | 'neutral' | 'negative';
}

const StoreMap: React.FC = () => {
  const [locations, setLocations] = useState<City[]>([]);
  const [viewport] = useState({
    latitude: 37.7749,
    longitude: -122.4194,
    zoom: 3,
  });
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  useEffect(() => {
    const fetchStoreSentiments = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userRows } = await supabase
        .from('users')
        .select('client_id')
        .eq('auth_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const userRow = userRows && userRows.length > 0 ? userRows[0] : null;

      if (!userRow?.client_id) return;

      const { data: stores } = await supabase
        .from('stores')
        .select('id, store_name, lat, lng, zip, hash_id')
        .eq('client_id', userRow.client_id)
        .eq('is_active', true);

      if (!stores) return;

      // Group stores by city
      const cityMap: Record<string, { name: string; state: string; lat: number; lng: number; storeIds: number[] }> = {};
      stores.forEach((store) => {
        const zipInfo = zipcodes.lookup(store.zip);
        const city = zipInfo?.city || 'Unknown';
        const state = zipInfo?.state || 'Unknown';
        let lat = store.lat;
        let lng = store.lng;
        if ((!lat || !lng) && zipInfo) {
          lat = zipInfo.latitude;
          lng = zipInfo.longitude;
        }
        if (!lat || !lng) return; // skip if no coordinates
        const cityKey = `${city},${state}`;
        if (!cityMap[cityKey]) {
          cityMap[cityKey] = {
            name: city,
            state,
            lat,
            lng,
            storeIds: [],
          };
        }
        cityMap[cityKey].storeIds.push(store.id);
      });

      // Get all store IDs
      const allStoreIds = stores.map((s) => s.id);
      const { data: sentiments } = await supabase
        .from('surveys')
        .select('store_id, sentiment')
        .in('store_id', allStoreIds);

      // Build a map of storeId -> { name, hash_id }
      const storeIdMap: Record<number, { name: string; hash_id: string }> = {};
      stores.forEach((store) => {
        storeIdMap[store.id] = { name: store.store_name, hash_id: store.hash_id };
      });

      // Aggregate sentiments by city
      const cityLocations: City[] = Object.values(cityMap).map((city) => {
        const cityStoreObjs = city.storeIds
          .map((id) => storeIdMap[id])
          .filter(Boolean);
        const citySentiments = sentiments
          ? sentiments
              .filter((s) => city.storeIds.includes(s.store_id))
              .map((s) => s.sentiment)
              .filter((s) => s === 'positive' || s === 'neutral' || s === 'negative')
          : [];
        // Debug log
        console.log(`[City Sentiment] ${city.name}, ${city.state}:`, citySentiments);
        // Scoring logic
        const score = citySentiments.reduce((acc, s) => {
          if (s === 'positive') return acc + 1;
          if (s === 'negative') return acc - 1;
          return acc;
        }, 0);
        const ratio = citySentiments.length ? score / citySentiments.length : 0;
        let sentiment: 'positive' | 'neutral' | 'negative' = 'neutral';
        if (ratio > 0.3) sentiment = 'positive';
        else if (ratio < -0.3) sentiment = 'negative';
        // Debug log
        console.log(`[City Score] ${city.name}, ${city.state}: score=${score}, ratio=${ratio}, sentiment=${sentiment}`);
        return {
          name: city.name,
          state: city.state,
          lat: city.lat,
          lng: city.lng,
          sentiment,
          storeIds: city.storeIds,
          stores: cityStoreObjs,
          avgSentiment: sentiment,
        };
      });

      setLocations(cityLocations);
    };

    fetchStoreSentiments();
  }, []);

  const getMarkerColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'green';
      case 'neutral': return 'yellow';
      case 'negative': return 'red';
      default: return 'gray';
    }
  };

  const calculateStateColor = (stateName: string) => {
    const stateLocations = locations.filter((loc) => loc.state === stateName);
    const sentimentScore = stateLocations.reduce((score, loc) => {
      switch (loc.sentiment) {
        case 'positive': return score + 1;
        case 'negative': return score - 1;
        default: return score;
      }
    }, 0);
    if (sentimentScore > 0) {
      return `rgba(108, 235, 129, ${Math.min(sentimentScore / stateLocations.length, 1)})`;
    } else if (sentimentScore < 0) {
      return `rgba(220, 53, 69, ${Math.min(Math.abs(sentimentScore) / stateLocations.length, 1)})`;
    }
    return '#FFD700';
  };

  // Emoji for sentiment
  const sentimentEmoji = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return '🟢';
      case 'neutral': return '🟡';
      case 'negative': return '🔴';
      default: return '⚪️';
    }
  };

  // Modal content for selected city
  const renderCityModal = () => {
    if (!selectedCity) return null;
    return (
      <div className="city-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.45)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="city-modal" style={{ background: '#fff', borderRadius: 16, padding: '2.5em 2em', minWidth: 320, maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative' }}>
          <button onClick={() => setSelectedCity(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
          <div style={{ fontWeight: 700, fontSize: '1.25em', marginBottom: 8 }}>{selectedCity.name}, {selectedCity.state}</div>
          <div style={{ marginBottom: 10 }}>
            <span style={{ fontWeight: 500 }}>Stores:</span>
            <ul style={{ margin: '0 0 0 1em', padding: 0 }}>
              {selectedCity.stores && selectedCity.stores.length > 0 ? (
                selectedCity.stores.map((s) => (
                  <li key={s.hash_id} style={{ marginBottom: 4 }}>
                    <span>{s.name}</span>
                    <a href={`https://app.vivvy.ai/checkin/${s.hash_id}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, fontSize: '0.97em', textDecoration: 'underline', color: '#249795' }}>Check-In Page</a>
                  </li>
                ))
              ) : (
                <li>No stores found</li>
              )}
            </ul>
          </div>
          <div style={{ marginTop: 10, fontSize: '1.08em' }}>
            <span style={{ fontWeight: 500 }}>Average Sentiment:</span>
            <span style={{ marginLeft: 8 }}>{selectedCity.avgSentiment} {sentimentEmoji(selectedCity.avgSentiment)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="map-section">
      <h2>Store Locations</h2>
      <MapboxMap
        locations={locations.map(city => ({
          id: city.name + ',' + city.state,
          name: city.name,
          lat: city.lat,
          lng: city.lng,
          sentiment: city.sentiment,
          stores: city.stores,
          avgSentiment: city.avgSentiment,
        }))}
        viewport={viewport}
        calculateStateColor={calculateStateColor}
        getMarkerColor={getMarkerColor}
        getCityPopupContent={(location) => {
          const city = locations.find(c => c.name === location.name);
          if (!city) return '<div style="padding: 10px;">City not found</div>';
          
          const popupContent = `
            <div style="padding: 20px; max-width: 360px; font-family: 'Outfit', sans-serif; position: relative;">
              <!-- Shimmer effect overlay -->
              <div style="position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent); animation: shimmer 2s infinite;"></div>
              
              <!-- Header with gradient text -->
              <div style="margin-bottom: 15px; text-align: center;">
                <h3 style="margin: 0; font-size: 18px; font-weight: 700; background: linear-gradient(135deg, #33a1b5, #68dbb8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
                  📍 ${city.name}, ${city.state}
                </h3>
              </div>
              
              <!-- Stores section -->
              <div style="margin-bottom: 15px; background: linear-gradient(135deg, rgba(51, 161, 181, 0.05), rgba(104, 219, 184, 0.05)); border-radius: 12px; padding: 12px;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                  <span style="font-size: 16px;">🏪</span>
                  <strong style="color: #374151; font-size: 14px;">Stores</strong>
                </div>
                <ul style="margin: 0; padding: 0; list-style: none;">
                  ${city.stores && city.stores.length > 0 
                    ? city.stores.map(s => `
                      <li style="margin-bottom: 8px; padding: 8px; background: white; border-radius: 8px; border-left: 3px solid #33a1b5; box-shadow: 0 2px 4px rgba(0,0,0,0.05); transition: all 0.3s ease;">
                        <div style="font-weight: 500; color: #1f2937; margin-bottom: 4px;">${s.name}</div>
                        <a href="https://app.vivvy.ai/checkin/${s.hash_id}" target="_blank" style="color: #33a1b5; text-decoration: none; font-size: 12px; font-weight: 500; display: inline-flex; align-items: center; gap: 4px;">
                          🔗 Check-In Page
                        </a>
                      </li>
                    `).join('')
                    : '<li style="color: #6b7280; font-style: italic; text-align: center; padding: 8px;">No stores found</li>'
                  }
                </ul>
              </div>
              
              <!-- Sentiment section -->
              <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.8)); border-radius: 12px; border: 1px solid rgba(51, 161, 181, 0.1);">
                <span style="font-size: 20px; animation: pulse 2s ease-in-out infinite;">${sentimentEmoji(city.avgSentiment)}</span>
                <div>
                  <div style="font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">Average Sentiment</div>
                  <div style="font-weight: 600; color: #1f2937; text-transform: capitalize;">${city.avgSentiment}</div>
                </div>
              </div>
              
              <!-- Floating particles effect -->
              <div style="position: absolute; top: 10px; right: 10px; font-size: 12px; opacity: 0.6;">✨</div>
              <div style="position: absolute; bottom: 10px; left: 10px; font-size: 10px; opacity: 0.4;">💫</div>
            </div>
          `;
          
          console.log('Popup content generated:', popupContent);
          return popupContent;
        }}
        onMarkerClick={(city) => setSelectedCity(locations.find(c => c.name === city.name && c.state === city.state) || null)}
      />
      {renderCityModal()}
    </div>
  );
};

export default StoreMap;
